// src/hooks/useSubscriptionStatus.js
import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook to get subscription status and details
 * @returns {Object} Subscription status and details
 */
export const useSubscriptionStatus = () => {
  const { user } = useAuth();

  const subscription = useMemo(() => {
    return user?.subscription || null;
  }, [user]);

  const isActive = useMemo(() => {
    if (!subscription) return false;
    return subscription.status === 'active' || subscription.status === 'trial';
  }, [subscription]);

  const isExpired = useMemo(() => {
    if (!subscription) return false;
    return subscription.status === 'expired';
  }, [subscription]);

  const isCancelled = useMemo(() => {
    if (!subscription) return false;
    return subscription.status === 'cancelled';
  }, [subscription]);

  const isSuspended = useMemo(() => {
    if (!subscription) return false;
    return subscription.status === 'suspended';
  }, [subscription]);

  const daysRemaining = useMemo(() => {
    if (!subscription || !subscription.nextBillingDate) return null;

    const now = new Date();
    const nextBilling = new Date(subscription.nextBillingDate);
    const diffTime = nextBilling - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  }, [subscription]);

  const isExpiringSoon = useMemo(() => {
    if (!daysRemaining) return false;
    return daysRemaining <= 7 && daysRemaining > 0;
  }, [daysRemaining]);

  const shouldShowWarning = useMemo(() => {
    return isExpiringSoon && isActive;
  }, [isExpiringSoon, isActive]);

  const shouldShowError = useMemo(() => {
    return isExpired || isCancelled || isSuspended;
  }, [isExpired, isCancelled, isSuspended]);

  return {
    subscription,
    isActive,
    isExpired,
    isCancelled,
    isSuspended,
    daysRemaining,
    isExpiringSoon,
    shouldShowWarning,
    shouldShowError,
    hasSubscription: !!subscription
  };
};